import { useEffect, useId, useMemo, useRef } from 'react';
import type { ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

import { useFocusTrap, type InitialFocusTarget } from '../../../hooks/useFocusTrap';
import { useReturnFocus } from '../../../hooks/useReturnFocus';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  initialFocus?: 'first' | 'close' | RefObject<HTMLElement>;
  dismissOnOverlayClick?: boolean;
  dismissOnEsc?: boolean;
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  initialFocus = 'first',
  dismissOnOverlayClick = true,
  dismissOnEsc = true,
}: DialogProps) {
  const instanceId = useId().replace(/:/g, '');
  const titleId = `dlg-${instanceId}-title`;
  const descriptionId = useMemo(
    () => (description ? `dlg-${instanceId}-description` : undefined),
    [description, instanceId],
  );
  const panelRef = useRef<HTMLDivElement>(null);

  useReturnFocus(open);
  useFocusTrap(open, panelRef, initialFocus as InitialFocusTarget);

  useEffect(() => {
    if (!open) {
      return;
    }

    document.body.classList.add('dialog-open');
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.classList.remove('dialog-open');
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !dismissOnEsc) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      onClose();
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [dismissOnEsc, onClose, open]);

  if (!open) {
    return null;
  }

  const widthClass = size === 'sm' ? 'max-w-md' : size === 'lg' ? 'max-w-3xl' : 'max-w-lg';

  return createPortal(
    <div
      className="fixed inset-0 z-[80] grid place-items-center bg-black/45 p-4 animate-fade-in motion-reduce:animate-none"
      onClick={(event) => {
        if (dismissOnOverlayClick && event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className={`w-full ${widthClass} rounded-xl border border-[color:var(--color-border-strong)] bg-[color:var(--color-surface-raised)] shadow-overlay outline-none`}
      >
        <header className="flex items-start justify-between gap-3 border-b border-[color:var(--color-border-subtle)] px-5 py-4">
          <h2 id={titleId} className="text-base font-semibold text-[color:var(--color-text-primary)]">{title}</h2>
          <button
            type="button"
            aria-label="Close dialog"
            onClick={onClose}
            className="rounded-md p-1 text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-hover-overlay)]"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </header>

        {description ? (
          <p id={descriptionId} className="px-5 pt-3 text-sm text-[color:var(--color-text-secondary)]">{description}</p>
        ) : null}

        {children ? <div className="px-5 py-4">{children}</div> : null}

        {footer ? (
          <footer className="flex items-center justify-end gap-2 border-t border-[color:var(--color-border-subtle)] px-5 py-3">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
